import { useState, useEffect } from 'react'
import {
    Users,
    Tag,
    BookOpen,
    DollarSign,
    TrendingUp,
    Building2,
    CreditCard,
    BarChart3,
    Activity,
    Calendar,
    AlertCircle
} from 'lucide-react'
import { supabase } from '../services/supabase'

interface DashboardStats {
    totalUsuarios: number
    totalCupons: number
    totalCursos: number
    totalVendas: number
    receitaTotal: number
    assinaturasAtivas: number
    imoveisCadastrados: number
    vendasEsteMes: number
    receitaEsteMes: number
}

interface RecentActivity {
    id: string
    tipo: 'venda' | 'assinatura' | 'cupom' | 'usuario'
    descricao: string
    data: string
    valor?: number
}

export default function AdminDashboard() {
    const [stats, setStats] = useState<DashboardStats>({
        totalUsuarios: 0,
        totalCupons: 0,
        totalCursos: 0,
        totalVendas: 0,
        receitaTotal: 0,
        assinaturasAtivas: 0,
        imoveisCadastrados: 0,
        vendasEsteMes: 0,
        receitaEsteMes: 0
    })
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadDashboardData()
    }, [])

    const loadDashboardData = async () => {
        try {
            setLoading(true)
            console.log('📊 Carregando dados do dashboard...')

            // Carregar estatísticas básicas
            // Usar corretores ao invés de usuarios
            const [
                corretoresResult,
                cuponsResult,
                cursosResult,
                vendasResult,
                assinaturasResult,
                imoveisResult
            ] = await Promise.all([
                supabase.from('corretores').select('id', { count: 'exact' }),
                supabase.from('cupons').select('id', { count: 'exact' }),
                supabase.from('cursos').select('id', { count: 'exact' }),
                supabase.from('vendas').select('id, valor_comissao', { count: 'exact' }),
                supabase.from('assinaturas').select('id', { count: 'exact' }).eq('status', 'ativa'),
                // Tentar usar função RPC para imóveis (se existir)
                (async () => {
                    try {
                        const { data: rpcData, error: rpcError } = await supabase.rpc('buscar_imoveis_admin')
                        if (!rpcError && rpcData) {
                            return { count: rpcData.length, data: rpcData, error: null }
                        }
                    } catch (e) {
                        console.log('RPC não disponível, usando query direta')
                    }
                    return await supabase.from('imoveis').select('id', { count: 'exact' })
                })()
            ])

            console.log('📊 Resultados das queries:', {
                corretores: corretoresResult.count,
                cupons: cuponsResult.count,
                cursos: cursosResult.count,
                vendas: vendasResult.count,
                assinaturas: assinaturasResult.count,
                imoveis: imoveisResult.count
            })

            // Calcular receita total a partir dos pagamentos das assinaturas (status='pago')
            let receitaTotal = 0
            let offsetTotal = 0
            const limit = 1000
            let hasMoreTotal = true

            while (hasMoreTotal) {
                const pagamentosTotalResult = await supabase
                    .from('pagamentos')
                    .select('valor')
                    .eq('status', 'pago')
                    .range(offsetTotal, offsetTotal + limit - 1)

                if (pagamentosTotalResult.data && pagamentosTotalResult.data.length > 0) {
                    const receitaPagina = pagamentosTotalResult.data.reduce(
                        (acc, pagamento) => acc + (pagamento.valor || 0),
                        0
                    )
                    receitaTotal += receitaPagina
                    offsetTotal += limit
                    hasMoreTotal = pagamentosTotalResult.data.length === limit
                } else {
                    hasMoreTotal = false
                }
            }

            // Calcular vendas e receita do mês atual
            const inicioMes = new Date()
            inicioMes.setDate(1)
            inicioMes.setHours(0, 0, 0, 0)
            const inicioMesISO = inicioMes.toISOString()

            // Buscar contagem de vendas do mês
            const vendasMesCountResult = await supabase
                .from('vendas')
                .select('id', { count: 'exact', head: true })
                .gte('created_at', inicioMesISO)

            const vendasEsteMes = vendasMesCountResult.count || 0

            // Calcular receita do mês atual a partir dos pagamentos das assinaturas (status='pago')
            let receitaEsteMes = 0
            let offsetMes = 0
            let hasMoreMes = true

            while (hasMoreMes) {
                const pagamentosMesResult = await supabase
                    .from('pagamentos')
                    .select('valor, data_pagamento')
                    .eq('status', 'pago')
                    .gte('data_pagamento', inicioMesISO)
                    .range(offsetMes, offsetMes + limit - 1)

                if (pagamentosMesResult.data && pagamentosMesResult.data.length > 0) {
                    const receitaPagina = pagamentosMesResult.data.reduce(
                        (acc, pagamento) => acc + (pagamento.valor || 0),
                        0
                    )
                    receitaEsteMes += receitaPagina
                    offsetMes += limit
                    hasMoreMes = pagamentosMesResult.data.length === limit
                } else {
                    hasMoreMes = false
                }
            }

            console.log('📊 Receitas (Assinaturas):', {
                receitaTotal,
                receitaEsteMes,
                inicioMes: inicioMesISO,
                vendasEsteMes
            })

            setStats({
                totalUsuarios: corretoresResult.count || 0,
                totalCupons: cuponsResult.count || 0,
                totalCursos: cursosResult.count || 0,
                totalVendas: vendasResult.count || 0,
                receitaTotal,
                assinaturasAtivas: assinaturasResult.count || 0,
                imoveisCadastrados: imoveisResult.count || 0,
                vendasEsteMes,
                receitaEsteMes
            })

            console.log('✅ Estatísticas carregadas:', {
                totalUsuarios: corretoresResult.count || 0,
                totalCupons: cuponsResult.count || 0,
                totalCursos: cursosResult.count || 0,
                totalVendas: vendasResult.count || 0,
                receitaTotal,
                assinaturasAtivas: assinaturasResult.count || 0,
                imoveisCadastrados: imoveisResult.count || 0,
                vendasEsteMes,
                receitaEsteMes
            })

            // Carregar atividades recentes
            await loadRecentActivity()

        } catch (error) {
            console.error('❌ Erro ao carregar dados do dashboard:', error)
        } finally {
            setLoading(false)
        }
    }

    const loadRecentActivity = async () => {
        try {
            console.log('📋 Carregando atividades recentes...')

            // Buscar vendas recentes
            const { data: vendasRecentes, error: vendasError } = await supabase
                .from('vendas')
                .select('id, created_at, valor_comissao')
                .order('created_at', { ascending: false })
                .limit(5)

            if (vendasError) {
                console.error('Erro ao buscar vendas recentes:', vendasError)
            }

            // Buscar assinaturas recentes
            const { data: assinaturasRecentes, error: assinaturasError } = await supabase
                .from('assinaturas')
                .select('id, created_at')
                .order('created_at', { ascending: false })
                .limit(5)

            if (assinaturasError) {
                console.error('Erro ao buscar assinaturas recentes:', assinaturasError)
            }

            // Buscar cupons usados recentemente
            const { data: cuponsUsados, error: cuponsError } = await supabase
                .from('cupons')
                .select('id, codigo, ultimo_uso')
                .not('ultimo_uso', 'is', null)
                .order('ultimo_uso', { ascending: false })
                .limit(5)

            if (cuponsError) {
                console.error('Erro ao buscar cupons usados:', cuponsError)
            }

            // Buscar corretores recentes (ao invés de usuarios)
            const { data: corretoresRecentes, error: corretoresError } = await supabase
                .from('corretores')
                .select('id, created_at, nome')
                .order('created_at', { ascending: false })
                .limit(5)

            if (corretoresError) {
                console.error('Erro ao buscar corretores recentes:', corretoresError)
            }

            const activities: RecentActivity[] = []

            // Adicionar vendas
            vendasRecentes?.forEach(venda => {
                activities.push({
                    id: venda.id,
                    tipo: 'venda',
                    descricao: 'Nova venda registrada',
                    data: venda.created_at,
                    valor: venda.valor_comissao
                })
            })

            // Adicionar assinaturas
            assinaturasRecentes?.forEach(assinatura => {
                activities.push({
                    id: assinatura.id,
                    tipo: 'assinatura',
                    descricao: 'Nova assinatura ativa',
                    data: assinatura.created_at
                })
            })

            // Adicionar cupons usados
            cuponsUsados?.forEach(cupom => {
                activities.push({
                    id: cupom.id,
                    tipo: 'cupom',
                    descricao: `Cupom ${cupom.codigo} utilizado`,
                    data: cupom.ultimo_uso || ''
                })
            })

            // Adicionar corretores (usuários)
            corretoresRecentes?.forEach(corretor => {
                activities.push({
                    id: corretor.id,
                    tipo: 'usuario',
                    descricao: `Novo corretor: ${corretor.nome}`,
                    data: corretor.created_at
                })
            })

            // Ordenar por data e pegar os 10 mais recentes
            activities.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
            setRecentActivity(activities.slice(0, 10))

        } catch (error) {
            console.error('Erro ao carregar atividades recentes:', error)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getActivityIcon = (tipo: string) => {
        switch (tipo) {
            case 'venda':
                return <DollarSign className="w-4 h-4 text-green-500" />
            case 'assinatura':
                return <CreditCard className="w-4 h-4 text-blue-500" />
            case 'cupom':
                return <Tag className="w-4 h-4 text-purple-500" />
            case 'usuario':
                return <Users className="w-4 h-4 text-orange-500" />
            default:
                return <Activity className="w-4 h-4 text-gray-500" />
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    return (
        <div className="dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-4">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-3xl font-bold leading-7 text-gray-900 dark:text-white">
                            Dashboard Administrativo
                        </h1>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Visão geral do sistema e métricas principais
                        </p>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            onClick={loadDashboardData}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <Activity className="w-4 h-4 mr-2" />
                            Atualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
                {/* Total Usuários */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Users className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Total de Usuários
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.totalUsuarios}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Total Cupons */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Tag className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Total de Cupons
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.totalCupons}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Total Cursos */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <BookOpen className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Total de Cursos
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.totalCursos}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Assinaturas Ativas */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <CreditCard className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Assinaturas Ativas
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.assinaturasAtivas}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Receita Total */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DollarSign className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Receita Total
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {formatCurrency(stats.receitaTotal)}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Vendas Este Mês */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <TrendingUp className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Vendas Este Mês
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.vendasEsteMes}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Receita Este Mês */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <BarChart3 className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Receita Este Mês
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {formatCurrency(stats.receitaEsteMes)}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Imóveis Cadastrados */}
                <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Building2 className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                            <dl>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Imóveis Cadastrados
                                </dt>
                                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                                    {stats.imoveisCadastrados}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Atividades Recentes
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        Últimas 10 atividades
                    </span>
                </div>
                <div className="flow-root">
                    <ul className="-mb-8">
                        {recentActivity.map((activity, activityIdx) => (
                            <li key={activity.id}>
                                <div className="relative pb-8">
                                    {activityIdx !== recentActivity.length - 1 ? (
                                        <span
                                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-600"
                                            aria-hidden="true"
                                        />
                                    ) : null}
                                    <div className="relative flex space-x-3">
                                        <div>
                                            <span className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center ring-8 ring-white dark:ring-gray-800">
                                                {getActivityIcon(activity.tipo)}
                                            </span>
                                        </div>
                                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {activity.descricao}
                                                    {activity.valor && (
                                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                                            {formatCurrency(activity.valor)}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                                <Calendar className="w-4 h-4 inline mr-1" />
                                                {formatDate(activity.data)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                {recentActivity.length === 0 && (
                    <div className="text-center py-6">
                        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                            Nenhuma atividade recente
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            As atividades aparecerão aqui conforme o sistema for usado.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}